/** Reports - the library of standard data reports (ADR-0212 decision 6b), at `/data-reports`.
 *
 * Two views of one place. **The library** is a set of cards, one per standard report (by scope, by
 * site, by vendor, by month, year on year, data quality, estimates, missing data, the evidence list,
 * factors used, energy use, intensity, supplier share and the audit trail); picking one writes
 * `?report=` so a report is a link. **A report** is dates and filters on one line over one table,
 * with Export CSV and Export workbook beside them.
 *
 * **Nothing here is a filing.** No readiness, no verdict, no regime and no percentage: the filing
 * pages are Filings (`/reports`). Every export says *Working report, not a filing* (the server writes
 * it) and carries the period, the Scope 2 basis and the time it ran. A report that cannot be produced
 * (intensity needs a stated denominator) is a card that says so, not an absent one.
 *
 * The catalogue and every row come from the server; this file draws them. The explanations behind
 * the small controls are `infoText.ts`'s (`reports.*`) or the server's own notes, which are shown in
 * a drawer so the page stays a title, one line and one table.
 */

import { ChartBar } from "@phosphor-icons/react";
import { useSearch } from "@tanstack/react-router";

import { useReportCatalogue } from "../../api/dataReports";
import { ReadFailed } from "../../components/Loaded";
import { TablePage } from "../../components/ui/TablePage";
import { RequireSession } from "../../shell/RequireSession";
import { ScreenFrame } from "../ScreenFrame";
import type { ScreenSearch } from "../search";
import { useScreen } from "../useScreen";
import { ReportLibrary } from "./ReportLibrary";
import { ReportRunView } from "./ReportRunView";

export function DataReportsScreen() {
  return (
    <RequireSession>
      <DataReportsBody />
    </RequireSession>
  );
}

function DataReportsBody() {
  const screen = useScreen();
  const search: Partial<ScreenSearch> = useSearch({ strict: false });
  const catalogue = useReportCatalogue();
  const key = search.report;
  const entry = key ? catalogue.data?.reports.find((candidate) => candidate.key === key) : undefined;

  return (
    <ScreenFrame screen={screen} title="Reports">
      {catalogue.error ? (
        <TablePage
          icon={ChartBar}
          title="Reports"
          state={
            <ReadFailed
              of="The list of reports"
              error={catalogue.error}
              onRetry={() => void catalogue.refetch()}
            />
          }
        />
      ) : key && entry ? (
        <ReportRunView screen={screen} entry={entry} />
      ) : (
        <ReportLibrary
          reports={catalogue.data?.reports}
          loading={catalogue.isPending}
          unknownKey={key !== undefined && catalogue.data !== undefined}
        />
      )}
    </ScreenFrame>
  );
}
